<template>
  <UIDialog
    :is-open
    :z-index
    :title="`Add tag to the document ${tagsEditDocumentName}`"
    size="sm"
    @close="handleClose"
  >
    <TagsListDropdown
      v-model="selectedTagId"
      :selected
      data-refid="tagsListDropdown"
      @update="handleRenameTag"
    />
    <UIButtonsGroup>
      <UIButtonClose @click="handleClose" />
      <UIButtonSave
        label="Add"
        :disabled="!selectedTagId"
        @click="handleClickAddTag"
      />
    </UIButtonsGroup>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { DocumentClass } from '..'
import { ModalClass } from '@types'

import { handleCatchedError } from '@/helpers/common'
import { NOTIFICATION_DELAY } from '@/const/common'
import { DOCUMENT_FIELD } from '../utils/const'

import { useNotifications } from '@/plugins/notification'
import { useDocumentsTagsStore } from '@/store/documents/tags'

import { UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import { UIDialog } from '@ui/modals'
import TagsListDropdown from './TagsListDropdown.vue'

type Props = {
  modal: ModalClass<any>
  instance: DocumentClass
}

const props = defineProps<Props>()

const { progress, remove, update } = useNotifications()
const documentsTagsStore = useDocumentsTagsStore()

const selectedTagId = ref<string>()

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const tagsEditDocumentName = computed(
  () => props.instance.field(DOCUMENT_FIELD.NAME).value,
)

const handleClose = () => {
  props.modal.close()
}

const handleRenameTag = async (id: string, name: string) => {
  const nid = await progress({
    message: 'Updating tag',
  })
  try {
    const result = await documentsTagsStore.update({
      id,
      name,
    })
    if (nid) {
      await update(
        nid,
        {
          type: 'success',
          message: `Tag ${result?.name} updated`,
        },
        NOTIFICATION_DELAY,
      )
    }
  } catch (e) {
    if (nid) await remove(nid)
    handleCatchedError(e as string, { id, name })
  }
}

const selected = computed({
  get() {
    return props.instance.field<string[]>(DOCUMENT_FIELD.TAGS).value
  },
  set(value) {
    props.instance.field<string[]>(DOCUMENT_FIELD.TAGS).value = [...value]
  },
})

const handleClickAddTag = async () => {
  if (!selectedTagId.value || selected.value.includes(selectedTagId.value))
    return

  selected.value = [...selected.value, selectedTagId.value]
  selectedTagId.value = undefined
  handleClose()
}
</script>
